// Classical constellation line overlay. Connects the brightest
// catalogue stars into the IAU stick-figure patterns and supports
// hover lookup so each constellation can show its name + a short
// description in the existing hover-label DOM.
//
// The figures themselves (RA hours, Dec degrees of the bright named stars,
// from SIMBAD / Bright Star Catalog, and the pairs to join) are plain data
// in src/data/constellationFigures.js. The factory builds:
//   - one LineSegments mesh holding all line geometry (cheap render)
//   - one invisible "hit-target" Mesh per constellation, sized to the
//     constellation's angular footprint, used for raycaster hover
//     detection from main.js. The hit-target carries the
//     constellation's userData for the hover label.

import * as THREE from 'three';
import { raDecToScene } from './sky/frame.js';
import { CONSTELLATIONS } from './data/constellationFigures.js';

export function createConstellations(shellRadius) {
  const group = new THREE.Group();
  group.name = 'constellations';
  group.renderOrder = -997;
  group.frustumCulled = false;
  group.visible = false;

  const lineRadius = shellRadius * 0.998;
  const hitTargets = [];

  // Single LineSegments for all line geometry — cheap to render.
  // Hover detection runs against per-constellation invisible spheres
  // that we sit beside the lines, sized to each constellation's
  // angular footprint.
  const positions = [];

  for (const c of CONSTELLATIONS) {
    let cx = 0, cy = 0, cz = 0;
    const endpoints = [];

    for (const seg of c.lines) {
      const a = raDecToScene(seg[0], seg[1]).multiplyScalar(lineRadius);
      const b = raDecToScene(seg[2], seg[3]).multiplyScalar(lineRadius);
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
      endpoints.push(a, b);
      cx += a.x + b.x; cy += a.y + b.y; cz += a.z + b.z;
    }

    const n = endpoints.length || 1;
    const center = new THREE.Vector3(cx / n, cy / n, cz / n);

    // Maximum distance from centroid to any endpoint — defines a
    // bounding sphere that comfortably covers the constellation's
    // line work for hover detection.
    let maxR = 0;
    for (const p of endpoints) {
      const d = p.distanceTo(center);
      if (d > maxR) maxR = d;
    }
    // Add a little padding so the user can hover anywhere over the
    // pattern, not only ON the lines.
    const hitRadius = maxR * 1.15;

    const hitGeo = new THREE.SphereGeometry(hitRadius, 12, 8);
    const hitMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.position.copy(center);
    hitMesh.name = `constellation-hit:${c.name}`;
    hitMesh.userData = {
      isConstellation: true,
      name: c.name,
      description: c.description
    };
    hitMesh.frustumCulled = false;
    hitMesh.visible = true;
    group.add(hitMesh);
    hitTargets.push(hitMesh);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(positions), 3)
  );

  const material = new THREE.LineBasicMaterial({
    color: 0x88aacc,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    depthTest: true
  });

  const lines = new THREE.LineSegments(geometry, material);
  lines.name = 'constellation-lines';
  lines.renderOrder = -997;
  lines.frustumCulled = false;
  group.add(lines);

  return {
    group,
    hitTargets,
    setVisible: (v) => { group.visible = !!v; },
    isVisible: () => group.visible,
    setOpacity: (a) => { material.opacity = Math.max(0, Math.min(1, a)); },
    dispose: () => {
      geometry.dispose();
      material.dispose();
      hitTargets.forEach((m) => {
        m.geometry.dispose();
        m.material.dispose();
      });
    }
  };
}

export { CONSTELLATIONS };
